import { useRef, useState } from 'react';
import type { Button } from '@livepuls/types';
import type { IntercomConfig, ExternalPort } from '@livepuls/types';
import { useExternalsStore } from '../../store/externals-store.js';

interface Props {
  button: Button;
  draft: IntercomConfig;
  /** Wird gerade gedrückt/aktiv → ganze Taste dunkelrot. */
  active?: boolean;
  /** Wird gerade angesprochen → orange hinterlegt/umrandet. */
  receiving?: boolean;
  onClick?: () => void;
  /**
   * Remote-Modus: aktueller Pegel (0–100) + Callback bei horizontalem Ziehen.
   * Ist onVolumeChange gesetzt, zeigt der Chip einen Pegelbalken und lässt
   * sich per Links-Rechts-Drag (Maus) regeln. Klick toggelt weiterhin.
   */
  volume?: number | undefined;
  onVolumeChange?: ((volume: number) => void) | undefined;
  /**
   * Aktiviert HTML5-Drag, damit der Button per Drop auf ein anderes Panel
   * dort als NEUER Link dupliziert werden kann (siehe PanelsPage / Backend
   * `POST /api/v1/buttons/:id/duplicate-to/:panelId`).
   */
  draggable?: boolean | undefined;
  /**
   * Wird gefeuert, wenn der Drag außerhalb jedes Drop-Targets endet
   * (= „aus dem Panel heraus gezogen"). Verwendung: Button löschen.
   */
  onDragOut?: (() => void | Promise<void>) | undefined;
  /**
   * Rechtsklick: liefert Bildschirm-Koordinaten an den Parent, der
   * dort ein Kontextmenü öffnet (Umbenennen, Bearbeiten, Kopieren …).
   */
  onContextMenu?: ((pos: { x: number; y: number }) => void) | undefined;
}

/**
 * Buttons sind LINK-Objekte. Das Label wird zur Renderzeit aus der Ziel-
 * Referenz abgeleitet (panel.name / partyline.name / externalId).
 */
function deriveLabel(b: Button, cfg: IntercomConfig, livePorts: ExternalPort[]): string {
  if (b.labelOverride) return b.labelOverride;
  switch (b.type) {
    case 'intercom':
      return cfg.panels.find((p) => p.id === b.targetPanelId)?.name ?? '?';
    case 'partyline':
      return cfg.partylines.find((p) => p.id === b.partylineId)?.name ?? '?';
    case 'listen':
      if (b.targetPanelId) return cfg.panels.find((p) => p.id === b.targetPanelId)?.name ?? '?';
      if (b.partylineId) return cfg.partylines.find((p) => p.id === b.partylineId)?.name ?? '?';
      return 'Listen';
    case 'call-to-all':
      return 'Alle';
    case 'external':
      return b.externalId;
    case 'external-port': {
      const port =
        livePorts.find((p) => p.key === b.portKey) ??
        cfg.externalPorts?.find((p) => p.key === b.portKey);
      return port?.label ?? 'Port';
    }
    case 'external-device':
      return cfg.externalDevices?.find((d) => d.id === b.externalDeviceId)?.name ?? 'Gerät';
  }
}

const TYPE_BADGE: Record<Button['type'], string> = {
  intercom: 'IC',
  partyline: 'PL',
  listen: 'LI',
  'call-to-all': 'CTA',
  external: 'EX',
  'external-port': 'XP',
  'external-device': 'XD',
};

export function ButtonChip({
  button,
  draft,
  active,
  receiving,
  onClick,
  draggable,
  onDragOut,
  onContextMenu,
  volume,
  onVolumeChange,
}: Props): JSX.Element {
  const livePorts = useExternalsStore((s) => s.ports);
  const label = deriveLabel(button, draft, livePorts);
  const mode = 'mode' in button ? button.mode : null;

  // --- Remote-Pegel per horizontalem Maus-Drag ---
  const [dragVol, setDragVol] = useState<number | null>(null);
  const draggedRef = useRef(false);
  const curVol = dragVol ?? volume ?? button.volume;

  function onVolPointerDown(e: React.PointerEvent<HTMLButtonElement>): void {
    if (!onVolumeChange) return;
    const el = e.currentTarget;
    const startX = e.clientX;
    const width = el.getBoundingClientRect().width || 1;
    const baseVol = volume ?? button.volume;
    draggedRef.current = false;
    let last = baseVol;
    const move = (ev: PointerEvent): void => {
      const dx = ev.clientX - startX;
      if (Math.abs(dx) > 4) draggedRef.current = true;
      if (draggedRef.current) {
        const next = Math.max(0, Math.min(100, Math.round(baseVol + (dx / width) * 100)));
        last = next;
        setDragVol(next);
      }
    };
    const up = (): void => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      if (draggedRef.current && last !== baseVol) onVolumeChange?.(last);
      setDragVol(null);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }

  const colors = active
    ? 'bg-[#5a1414] border-[#ef4444] text-white' // gedrückt → dunkelrot
    : receiving
    ? 'bg-[#3a2a06] border-[#f59e0b] text-[#fcd34d]' // angesprochen → orange
    : 'bg-elev border-border hover:bg-hover text-text';
  return (
    <button
      onClick={() => {
        // Nach einem Pegel-Drag NICHT zusätzlich togglen.
        if (draggedRef.current) {
          draggedRef.current = false;
          return;
        }
        onClick?.();
      }}
      onPointerDown={onVolumeChange ? onVolPointerDown : undefined}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu?.({ x: e.clientX, y: e.clientY });
      }}
      draggable={draggable}
      onDragStart={
        draggable
          ? (e) => {
              e.dataTransfer.effectAllowed = 'copyMove';
              e.dataTransfer.setData('application/x-livepuls-button-id', button.id);
              e.dataTransfer.setData('text/plain', button.id);
            }
          : undefined
      }
      onDragEnd={
        draggable && onDragOut
          ? (e) => {
              if (e.dataTransfer.dropEffect === 'none') void onDragOut();
            }
          : undefined
      }
      className={[
        'w-full h-full rounded-btn px-2 py-1.5 text-center',
        'border transition flex flex-col items-center justify-center gap-1',
        draggable ? 'cursor-grab active:cursor-grabbing' : '',
        colors,
      ].join(' ')}
      title={`${button.type} · vol ${button.volume}% · Rechtsklick für Menü`}
    >
      <span className="text-xxs uppercase tracking-wider opacity-60">
        {TYPE_BADGE[button.type]}
        {mode ? ` · ${mode}` : ''}
      </span>
      <span className="text-base font-semibold leading-tight break-words line-clamp-2">
        {label}
      </span>
      {/* Remote-Pegel: Balken + Wert, per Links-Rechts-Drag regelbar. */}
      {onVolumeChange ? (
        <div className="w-full mt-0.5">
          <div className="h-1.5 w-full rounded-full bg-black/40 overflow-hidden">
            <div
              className="h-full bg-accent transition-[width] duration-75"
              style={{ width: `${curVol}%` }}
            />
          </div>
          <div className="text-[10px] text-text-mute mt-0.5 tabular-nums">{curVol}%</div>
        </div>
      ) : null}
    </button>
  );
}
